import { ProfilePageType, ProfileProps } from '@common/types';
import Header from '@components/Header';
import ContactStatusToast from '@components/Profile/ContactStatusToast';
import BriefContactStatusModal from '@components/Profile/BriefContactStatusModal';
import ProfileTemplates from '@components/Profile/ProfileTemplates/ProfileTemplates';
import SectionWrapper from '@components/Profile/SectionWrapper';
import {
  PAGE_NAMES,
  PROFILE_PAGE_DATA_HOOKS,
} from '@utils/partnerProfileUtils';
import { Box } from '@wix/design-system';
import CopyNotification from '@components/Profile/CopyNotification';
import { FC } from 'react';
import {
  useProfileContext,
  ACTIONS,
} from '@components/Contexts/ProfileContext';
import About from './About';
import BackToExplorer from './BackToExplorer';
import ClientProjects from './ClientProjects';
import ContactPanel from './ContactPanel';
import NavBar from './NavBar';
import StartingRates from './StartingRates';
import TraceableTraderData from './TraceableTraderData';
import ProjectsReviews from './ProjectsReviews';
import { PROFILE_PAGE_SECTIONS } from '@utils/constants';
import { useProfileSections } from './hooks/useProfileSections';
import { useMarketplaceBriefAuditChanges } from '@components/Hooks/useMarketplaceBriefAuditChanges';
import s from './Profile.module.scss';

const ProfileDesktop: FC<ProfileProps> = ({
  onContactClicked,
  onCopyClicked,
  onScheduleMeetingClicked,
  headerOnClick,
  showBack,
  certifications,
  scrollToSection,
  sessionId,
  isRenderedInModal,
  numberOfTemplates,
  navigationRefs,
  copy,
  profileUrl,
  contactStatus,
  clearStatusMap,
}) => {
  const { copyStatus, dispatch, profilePageType } = useProfileContext();
  const {
    shouldShowClientProjects,
    shouldShowServices,
    shouldShowReviews,
    shouldShowTemplates,
    shouldShowServicesAndReviews,
    isLastSection,
  } = useProfileSections(numberOfTemplates);
  const { isAuditChangeEnabled } = useMarketplaceBriefAuditChanges();

  const setCopyStatus = (status: null | 'success' | 'error') => {
    dispatch({ type: ACTIONS.SET_COPY_STATUS, copyStatus: status });
  };

  return (
    <Box
      display="block"
      dataHook={PROFILE_PAGE_DATA_HOOKS.PROFILE_PAGE}
      backgroundColor="white"
      className={s.profileAccessibility}
    >
      {!isRenderedInModal ? (
        <div className={s.stickyHeader}>
          <Header
            onClick={headerOnClick}
            pageName={PAGE_NAMES.PROFILE_PAGE}
            sessionId={sessionId}
          />
        </div>
      ) : (
        <div className={s.stickyHeader}></div>
      )}
      <CopyNotification
        status={copyStatus}
        clearStatus={() => setCopyStatus(null)}
        isMobileView={false}
      />
      {isAuditChangeEnabled ? (
        <BriefContactStatusModal
          contactStatus={contactStatus}
          clearStatus={contactStatus ? clearStatusMap[contactStatus] : () => {}}
          isMobileView={false}
        />
      ) : (
        <ContactStatusToast
          status={contactStatus}
          clearStatus={contactStatus ? clearStatusMap[contactStatus] : () => {}}
          isMobileView={false}
        />
      )}
      <Box
        dataHook={PROFILE_PAGE_DATA_HOOKS.PROFILE_PAGE_CONTAINER}
        className={s.profilePageContainer}
        direction="horizontal"
      >
        <Box
          className={s.leftPanel}
          dataHook={PROFILE_PAGE_DATA_HOOKS.LEFT_PANEL}
          direction="vertical"
        >
          <BackToExplorer showBack={showBack} isMobileView={false} />
          <ContactPanel
            isMobileView={false}
            onContactClicked={onContactClicked}
            copy={copy}
            onCopyClicked={onCopyClicked}
            onScheduleMeetingClicked={onScheduleMeetingClicked}
            shareUrl={profileUrl}
            certifications={certifications}
            showProviderReviewSummary={
              profilePageType === ProfilePageType.MARKETPLACE ||
              profilePageType === ProfilePageType.MARKETPLACE_NO_LEADS
            }
          />
        </Box>

        <Box direction="vertical" width="100%">
          <NavBar
            isMobileView={false}
            navigationRefs={navigationRefs}
            showBack={showBack}
          />
          <Box
            dataHook="profile-page-sections"
            className={s.profilePageSections}
            direction="horizontal"
          >
            <div style={{ width: '100%' }}>
              <SectionWrapper
                sectionId={PROFILE_PAGE_SECTIONS.CLIENT_PROJECTS}
                ref={navigationRefs}
                visible={shouldShowClientProjects}
                showDivider={!isLastSection.clientProjects}
              >
                <ClientProjects
                  isMobileView={false}
                  onShowLess={() =>
                    scrollToSection(PROFILE_PAGE_SECTIONS.CLIENT_PROJECTS)
                  }
                />
              </SectionWrapper>

              <SectionWrapper
                ref={navigationRefs}
                sectionId={PROFILE_PAGE_SECTIONS.ABOUT}
                showDivider={!isLastSection.about}
              >
                <About
                  isMobileView={false}
                  certifications={certifications}
                  onShowLess={() =>
                    scrollToSection(PROFILE_PAGE_SECTIONS.ABOUT)
                  }
                />
              </SectionWrapper>

              <SectionWrapper
                sectionId={PROFILE_PAGE_SECTIONS.SERVICES_AND_REVIEWS}
                ref={navigationRefs}
                visible={shouldShowServicesAndReviews}
              >
                <SectionWrapper
                  sectionId={PROFILE_PAGE_SECTIONS.SERVICES}
                  ref={navigationRefs}
                  visible={shouldShowServices}
                  showDivider={!isLastSection.services}
                >
                  <StartingRates />
                </SectionWrapper>
                <SectionWrapper
                  visible={shouldShowReviews}
                  sectionId={PROFILE_PAGE_SECTIONS.REVIEWS}
                  ref={navigationRefs}
                  showDivider={!isLastSection.reviews}
                >
                  <ProjectsReviews
                    isMobileView={false}
                    onPageChange={() =>
                      scrollToSection(PROFILE_PAGE_SECTIONS.REVIEWS)
                    }
                  />
                </SectionWrapper>
              </SectionWrapper>
              <SectionWrapper
                sectionId={PROFILE_PAGE_SECTIONS.TEMPLATES}
                ref={navigationRefs}
                visible={shouldShowTemplates}
                showDivider={!isLastSection.templates}
              >
                <ProfileTemplates
                  isMobileView={false}
                  onShowLess={() =>
                    scrollToSection(PROFILE_PAGE_SECTIONS.TEMPLATES)
                  }
                />
              </SectionWrapper>
              <TraceableTraderData />
            </div>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProfileDesktop;
